import React, { useState, useRef, useEffect } from 'react'
import clsx from 'clsx'
import * as Popover from '@radix-ui/react-popover'
import { Filter, X, Search } from 'lucide-react'

// ---------------------------------------------------------------------------
// Filter value types
// ---------------------------------------------------------------------------

export type TextFilter = {
    type: 'text'
    value: string
}

export type ScoreThresholdFilter = {
    type: 'score-threshold'
    operator: '>=' | '<=' | '='
    value: number
}

export type DiscreteFilter = {
    type: 'discrete'
    selected: Set<string>
}

export type FilterValue = TextFilter | ScoreThresholdFilter | DiscreteFilter

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type BaseProps = {
    isActive: boolean
    onClear: () => void
}

type TextFilterProps = BaseProps & {
    kind: 'text'
    value: string
    onChange: (value: string) => void
}

type ScoreFilterProps = BaseProps & {
    kind: 'score'
    operator: '>=' | '<=' | '='
    value: number
    onOperatorChange: (op: '>=' | '<=' | '=') => void
    onValueChange: (v: number) => void
}

type DiscreteFilterProps = BaseProps & {
    kind: 'discrete'
    options: { label: string; value: string }[]
    selected: Set<string>
    onToggle: (value: string) => void
}

type Props = TextFilterProps | ScoreFilterProps | DiscreteFilterProps

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ColumnFilter(props: Props): React.JSX.Element {
    const [open, setOpen] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (open && props.kind === 'text' && inputRef.current) {
            // Small delay so popover has rendered
            const t = setTimeout(() => inputRef.current?.focus(), 50)
            return () => clearTimeout(t)
        }
    }, [open, props.kind])

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button
                    type="button"
                    className={clsx(
                        'inline-flex items-center justify-center rounded p-0.5 transition-colors',
                        'hover:bg-gray-200/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400',
                        props.isActive
                            ? 'text-blue-600'
                            : 'text-gray-400 opacity-0 group-hover/th:opacity-100',
                    )}
                    aria-label="Filter column"
                >
                    <Filter className="w-3 h-3" />
                </button>
            </Popover.Trigger>

            <Popover.Portal>
                <Popover.Content
                    side="bottom"
                    align="start"
                    sideOffset={6}
                    className={clsx(
                        'z-50 rounded-lg border border-gray-200 bg-white shadow-lg',
                        'w-56 p-3',
                        'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                    )}
                    onOpenAutoFocus={(e) => {
                        // Prevent Radix from stealing focus when we handle it ourselves
                        if (props.kind === 'text') e.preventDefault()
                    }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Filter
                        </span>
                        {props.isActive && (
                            <button
                                type="button"
                                onClick={() => {
                                    props.onClear()
                                    setOpen(false)
                                }}
                                className="text-[10px] text-gray-400 hover:text-red-500 transition-colors flex items-center gap-0.5"
                            >
                                <X className="w-3 h-3" />
                                Clear
                            </button>
                        )}
                    </div>

                    {props.kind === 'text' && (
                        <TextFilterContent
                            inputRef={inputRef}
                            value={props.value}
                            onChange={props.onChange}
                        />
                    )}

                    {props.kind === 'score' && (
                        <ScoreFilterContent
                            operator={props.operator}
                            value={props.value}
                            onOperatorChange={props.onOperatorChange}
                            onValueChange={props.onValueChange}
                        />
                    )}

                    {props.kind === 'discrete' && (
                        <DiscreteFilterContent
                            options={props.options}
                            selected={props.selected}
                            onToggle={props.onToggle}
                        />
                    )}
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TextFilterContent({
    inputRef,
    value,
    onChange,
}: {
    inputRef: React.RefObject<HTMLInputElement>
    value: string
    onChange: (v: string) => void
}): React.JSX.Element {
    return (
        <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
                ref={inputRef}
                type="text"
                placeholder="Search..."
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={clsx(
                    'w-full rounded-md border border-gray-200 bg-gray-50 text-sm',
                    'pl-7 pr-2 py-1.5',
                    'placeholder:text-gray-400',
                    'focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30',
                )}
            />
        </div>
    )
}

function ScoreFilterContent({
    operator,
    value,
    onOperatorChange,
    onValueChange,
}: {
    operator: '>=' | '<=' | '='
    value: number
    onOperatorChange: (op: '>=' | '<=' | '=') => void
    onValueChange: (v: number) => void
}): React.JSX.Element {
    const operators: { label: string; value: '>=' | '<=' | '=' }[] = [
        { label: '\u2265', value: '>=' },
        { label: '\u2264', value: '<=' },
        { label: '=', value: '=' },
    ]

    return (
        <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-gray-200 overflow-hidden">
                {operators.map((op) => (
                    <button
                        key={op.value}
                        type="button"
                        onClick={() => onOperatorChange(op.value)}
                        className={clsx(
                            'px-2 py-1 text-xs font-medium transition-colors',
                            operator === op.value
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-50 text-gray-600 hover:bg-gray-100',
                        )}
                    >
                        {op.label}
                    </button>
                ))}
            </div>
            <div className="flex items-center gap-1">
                <input
                    type="number"
                    min={0}
                    max={100}
                    value={value}
                    onChange={(e) => {
                        const n = parseInt(e.target.value, 10)
                        if (!isNaN(n))
                            onValueChange(Math.min(100, Math.max(0, n)))
                    }}
                    className={clsx(
                        'w-14 rounded-md border border-gray-200 bg-gray-50 text-sm text-center',
                        'py-1',
                        'focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30',
                    )}
                />
                <span className="text-xs text-gray-500">%</span>
            </div>
        </div>
    )
}

function DiscreteFilterContent({
    options,
    selected,
    onToggle,
}: {
    options: { label: string; value: string }[]
    selected: Set<string>
    onToggle: (value: string) => void
}): React.JSX.Element {
    return (
        <div className="flex flex-col gap-1">
            {options.map((opt) => (
                <label
                    key={opt.value}
                    className={clsx(
                        'flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer transition-colors',
                        'hover:bg-gray-50',
                        selected.has(opt.value) && 'bg-blue-50/60',
                    )}
                >
                    <input
                        type="checkbox"
                        checked={selected.has(opt.value)}
                        onChange={() => onToggle(opt.value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-400/30 w-3.5 h-3.5"
                    />
                    <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
            ))}
        </div>
    )
}
